import axios from 'axios';
import type { RegistrationData } from '@/stores/auth';
import { setupUserBlockInterceptor } from '@shared/utils/setupUserBlockInterceptor';

import type {
  AuthRequest,
  UserReportForm,
  UserPayload,
  CurrentUserResponse,
} from './auth.service.types';

const backendUrl = import.meta.env.VITE_AUTH_URL;

const commonHeaders = {
  'Content-Type': 'application/json',
};

const authApi = axios.create({
  baseURL: backendUrl,
  withCredentials: true,
  headers: { ...commonHeaders },
});

interface AuthUrlResponse {
  google: string;
  microsoft: string;
  upm: string;
  apple: string;
}

setupUserBlockInterceptor(authApi);

export const authService = {
  login(dataRq: AuthRequest) {
    return authApi.post<UserPayload>('/login', dataRq);
  },

  async exchangeHandoff(code: string) {
    return await authApi.post<{ sessionToken: string }>(
      '/session/handoff/exchange',
      { code },
    );
  },

  async logout() {
    await authApi.post('/logout');
  },

  async sendToken(newUserData: RegistrationData) {
    return await authApi.post('/send-token', newUserData);
  },

  async getCurrentUser() {
    return await authApi.get<CurrentUserResponse>('/current-user');
  },

  async refreshCookie() {
    return await authApi.post<UserPayload>('/refresh-cookie');
  },

  async getAuthUrl() {
    return await authApi.get<AuthUrlResponse>(`/public/url?type=all`);
  },
  sendCodeToRestorePassword(email: string) {
    return authApi.post(`/update-password-token`, { email: email });
  },
  validateCodeToRestorePassword(email: string, tokenCode: string) {
    return authApi.post(`/check-token`, { email: email, tokenCode: tokenCode });
  },
  async connectToRoom(room: string) {
    return await authApi.post<{ message: string }>('/room/connect', {
      productVirtualSlug: room,
    });
  },

  async refreshConnection(room: string) {
    return await authApi.post<{ message: string }>('/room/refresh', {
      productVirtualSlug: room,
    });
  },

  async sendUserReport(report: UserReportForm) {
    return await authApi.post('/report', report);
  },

  resetPassword(
    email: string,
    tokenCode: string,
    password: string,
    confirmPassword: string,
  ) {
    return authApi.post(`/update-password`, {
      email: email,
      tokenCode: tokenCode,
      password: password,
      confirmPassword: confirmPassword,
    });
  },
  deleteAccount() {
    return authApi.delete(`/account`);
  },
};
